import { useState } from 'react';
import { Cog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const CUSTOM_INSTRUCTIONS_KEY = 'capitol-builder-custom-instructions';

function loadCustomInstructions(): string {
  try {
    return localStorage.getItem(CUSTOM_INSTRUCTIONS_KEY) || '';
  } catch {
    return '';
  }
}

function persistCustomInstructions(value: string) {
  try {
    if (value) {
      localStorage.setItem(CUSTOM_INSTRUCTIONS_KEY, value);
    } else {
      localStorage.removeItem(CUSTOM_INSTRUCTIONS_KEY);
    }
  } catch {
    /* ignore */
  }
}

interface CustomInstructionsPopoverProps {
  customInstructions: string;
  onSave: (value: string) => void;
}

export { loadCustomInstructions, persistCustomInstructions };

export function CustomInstructionsPopover({
  customInstructions,
  onSave,
}: CustomInstructionsPopoverProps) {
  const [draft, setDraft] = useState(customInstructions);
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (isOpen) setDraft(customInstructions);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          className='relative flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors'
          title='Custom instructions'
        >
          <Cog className='size-3.5' />
          {customInstructions && (
            <span className='absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-blue-500' />
          )}
          <span>Custom instructions</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-96' side='top' align='start'>
        <div className='space-y-3'>
          <div>
            <Label
              htmlFor='custom-instructions'
              className='text-sm font-medium'
            >
              Custom Instructions
            </Label>
            <p className='text-xs text-muted-foreground mt-0.5'>
              These instructions are included with every message you send to the
              builder agent.
            </p>
          </div>
          <Textarea
            id='custom-instructions'
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder='e.g., Always use Claude Sonnet 4.5 for agent nodes. Prefer markdown output nodes.'
            rows={5}
            className='text-sm resize-none'
          />
          <div className='flex justify-between'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                setDraft('');
                onSave('');
                setOpen(false);
              }}
              disabled={!customInstructions && !draft}
            >
              Clear
            </Button>
            <Button
              size='sm'
              onClick={() => {
                onSave(draft);
                setOpen(false);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
